import React, { useState, useMemo, useEffect, useCallback } from 'react';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import {
    makeStyles,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Button,
    CircularProgress,
} from '@material-ui/core';
import Modal from './Modal';
import { useSelector, useDispatch } from 'react-redux';
import helpers from 'app/utils/helpers';
import certificationEndpoints from 'app/api/services/certificationEndpoints';
import notification from 'app/config/notification';
import { injectIntl } from 'react-intl';
import { clearFiles } from 'app/store/slices/forms';
import moment from 'moment';
import { showLoading, hideLoading } from 'app/store/slices/settings';
import { STATUS_CODES } from 'app/config/status';
import { updateRequestDetails } from 'app/store/slices/requests';
import requestEndpoints from 'app/api/services/requestEndpoints';
import { errorHandler } from 'app/utils/errorHandler';
import filesEndpoint from 'app/api/services/filesEndpoint';

import styles from './../styles';
import { downloadFile } from '../../../../../utils/forms/files';
const useStyles = makeStyles(styles);

function Certification(props) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const { intl, validateDocuments } = props;

    const [certificationSelected, setCertificationSelected] = useState({});
    const [canAddNew, setCanAddNew] = useState(true);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [updateFile, setUpdateFile] = useState(false);
    const [sending, setSending] = useState(false);

    const filesStore = useSelector((state) => state.forms.files);
    const request = useSelector((state) => state.requests.details);

    const requestJsonData = useMemo(() => {
        return request.jsonSolicitacao ? JSON.parse(request.jsonSolicitacao) : {};
    }, [request]);

    const canEdit = useMemo(() => {
        return request?.situacao?.boEdicaoPortalInterno;
    }, [request]);

    const [canEditCertificate, setCanEditCertificate] = useState(() => {
        return (
            canEdit &&
            (request.cdSituacao === STATUS_CODES.EM_ANALISE ||
                request.cdSituacao === STATUS_CODES.AGUARDANDO_AJUSTES_DOCUMENTO)
        );
    });

    const [canSendToAssignCertificate, setCanSendToAssignCertificate] = useState(() => {
        const response =
            canEdit &&
            (request.cdSituacao === STATUS_CODES.EM_ANALISE ||
                request.cdSituacao === STATUS_CODES.AGUARDANDO_AJUSTES_DOCUMENTO);
        return response;
    });

    const getCertificates = useCallback(async () => {
        const response = await certificationEndpoints.getCertificates(request.cdSolicitacao);
        if (response.status === 200) {
            const certificatesRows = response.data;
            setRows(certificatesRows);
            if (certificatesRows.length > 0) {
                setCanAddNew(false);
            }
        }
    }, [request]);

    useEffect(() => {
        dispatch(clearFiles());
        (async () => {
            setLoading(true);
            try {
                await getCertificates();
            } catch (error) {
                notification(intl.formatMessage({ id: 'MESSAGES.CERTIFICATION.PROBLEM_GET_CERTIFICATES' }), {
                    toastId: 'PROBLEM_GET_CERTIFICATES',
                    type: 'error',
                });
                errorHandler(error.response?.status, error.response?.data?.message);
            }
            setLoading(false);
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, getCertificates]);

    const handleClose = () => {
        setOpen(false);
        setUpdateFile(false);
        dispatch(clearFiles());
        setCertificationSelected(null);
    };

    const openModal = (row) => {
        const certificateRow = { ...row, updateFile: false };
        setCertificationSelected(certificateRow);
        setOpen(true);
    };

    const onSave = async (values, fromSubmit) => {
        if (!canEdit) return;
        try {
            setSending(true);
            const sendingData = new FormData();
            if (values.cdCertificado) sendingData.append('cdCertificado', values.cdCertificado);
            if (values.idFileCrc) sendingData.append('idFileCrc', values.idFileCrc);
            if (values.idGdoc) sendingData.append('idGdoc', values.idGdoc);
            sendingData.append('cdSolicitacao', request.cdSolicitacao);
            if (requestJsonData.cdTipoEmpresa) {
                sendingData.append('cdTipoEmpresa', requestJsonData.cdTipoEmpresa);
            }
            if (requestJsonData.cdTipoPessoaFisica) {
                sendingData.append('cdTipoPessoaFisica', requestJsonData.cdTipoPessoaFisica);
            }
            if (values.deObservacao) {
                sendingData.append('deObservacao', values.deObservacao);
            }

            sendingData.append('dtEmissaoCrc', `${moment.utc(values.dtEmissaoCrc).format('YYYY-MM-DD')} 12:00:00`);
            sendingData.append(
                'dtVencimentoCrc',
                values.dtVencimentoCrc ? `${moment.utc(values.dtVencimentoCrc).format('YYYY-MM-DD')} 12:00:00` : '',
            );
            if (filesStore[0]) {
                const convertedFile = helpers.dataURItoBlob(filesStore[0].file);
                const fileName = filesStore[0].fileName ? filesStore[0].fileName : filesStore[0].name;
                sendingData.append('file', convertedFile, fileName);
            } else {
                try {
                    if (values.idFileCrc && !values.updateFile) {
                        const fileData = await filesEndpoint.getFileData(values.idFileCrc);
                        if (fileData?.status === 200 && fileData?.data) {
                            const responseBlob = await filesEndpoint.getFileBlob(values.idFileCrc);
                            if (responseBlob?.status === 200 && responseBlob?.data) {
                                const blob = new Blob([responseBlob.data], { type: 'application/pdf' });
                                sendingData.append('file', blob, fileData.data.name);
                            }
                        }
                    }
                } catch (error) {}
            }
            const response = await certificationEndpoints.saveCertificate(sendingData);
            if (response.status === 200) {
                notification(intl.formatMessage({ id: 'MESSAGES.CERTIFICATION.SUCCESSFUL_SAVED' }), {
                    toastId: 'SUCCESSFUL_SAVED',
                    type: 'success',
                });
                await getCertificates();
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        }
        handleClose();
        setSending(false);
        return;
    };

    const sendToAssign = async (certificate) => {
        const documentsOK = await validateDocuments();
        if (!documentsOK) {
            notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.MISSING_DOCUMENTS_TO_APPROVE' }), {
                toastId: 'MISSING_DOCUMENTS_TO_APPROVE',
                type: 'warning',
            });
            dispatch(hideLoading());
            return;
        }

        try {
            dispatch(showLoading());
            const sendingData = new FormData();
            sendingData.append('cdCertificado', certificate.cdCertificado);
            sendingData.append('cdSolicitacao', certificate.cdSolicitacao);
            const response = await certificationEndpoints.signCertificate(sendingData);
            if (response.status === 200) {
                notification(intl.formatMessage({ id: 'MESSAGES.CERTIFICATION.SUCCESSFUL_SEND_SIGNATURE' }), {
                    toastId: 'SUCCESSFUL_SEND_SIGNATURE',
                    type: 'success',
                });
            }
            setCanEditCertificate(false);
            setCanSendToAssignCertificate(false);
            getCertificates();
            const responseRequest = await requestEndpoints.findRequest({ cdSolicitacao: request.cdSolicitacao }, '');
            if (responseRequest.status === 200 && responseRequest.data) {
                await dispatch(updateRequestDetails(responseRequest.data));
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        }
        dispatch(hideLoading());
    };

    const downloadFile_ = async (idFileCrc) => {
        await downloadFile({ idFile: idFileCrc, dispatch }, null);
    };

    return (
        <>
            <Modal
                {...props}
                classes={classes}
                open={open}
                onClose={handleClose}
                onSave={onSave}
                certification={certificationSelected}
                request={request}
                requestJsonData={requestJsonData}
                downloadFile={downloadFile_}
                updateFile={updateFile}
                setUpdateFile={setUpdateFile}
            />
            {loading && (
                <div className={classes.centerProgress}>
                    <CircularProgress size={20} />
                </div>
            )}
            {!loading && (
                <div className={classes.reponsiveTable}>
                    <Table className={classes.table} aria-label="customized table">
                        <TableHead>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={classes.headTableCell} align="center">
                                    Número
                                </TableCell>
                                <TableCell className={classes.headTableCell} align="center">
                                    Data Solicitação
                                </TableCell>
                                <TableCell className={classes.headTableCell} align="left">
                                    Tipo
                                </TableCell>
                                <TableCell className={classes.headTableCell} align="left"></TableCell>
                                <TableCell className={classes.headTableCell} align="left" style={{ minWidth: '110px' }}>
                                    {!sending && canEditCertificate && canAddNew && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disableElevation
                                            className={classes.addButton}
                                            onClick={() => openModal(null)}>
                                            <AddIcon /> Adicionar
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.numeroCrc} className={classes.tableRow}>
                                    <TableCell align="center" component="th" scope="row">
                                        {String(row.numeroCrc).padStart(3, '0')}
                                    </TableCell>
                                    <TableCell align="center">
                                        {moment.utc(row.dtSolicitacao).format('DD/MM/YYYY')}
                                    </TableCell>
                                    <TableCell align="left">{request?.servico?.nmServico}</TableCell>
                                    <TableCell align="right">
                                        {row.boAssinado && (
                                            <Button size="small" className={classes.btnSignatureCompany}>
                                                <img alt="" src={process.env.PUBLIC_URL + '/img/signature.png'} />
                                                Documento assinado
                                            </Button>
                                        )}
                                        {!row.boAssinado && canSendToAssignCertificate && (
                                            <Button
                                                size="small"
                                                className={classes.btnSignatureCompany}
                                                onClick={() => sendToAssign(row)}>
                                                <img
                                                    alt=""
                                                    src={process.env.PUBLIC_URL + '/img/signature.png'}
                                                    style={{ height: '30px' }}
                                                />
                                                Enviar para assinatura
                                            </Button>
                                        )}
                                        {!row.boAssinado && !canSendToAssignCertificate && (
                                            <Button size="small" className={classes.btnSignature}>
                                                <img
                                                    alt=""
                                                    src={process.env.PUBLIC_URL + '/img/signature.png'}
                                                    style={{ height: '30px' }}
                                                />
                                                Aguardando assinatura
                                            </Button>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {row.idFileCrc && (
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                className={classes.btnSigleAction}
                                                size="small"
                                                onClick={() => downloadFile_(row.idFileCrc)}>
                                                <PictureAsPdfIcon fontSize="inherit" />
                                            </Button>
                                        )}
                                        {canEditCertificate && (
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                className={classes.btnSigleAction}
                                                size="small"
                                                onClick={() => openModal(row)}>
                                                <EditIcon fontSize="inherit" />
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </>
    );
}

export default injectIntl(Certification);
